import React, { useState, useContext } from "react"
import { Avatar, Box, Typography, useTheme } from "@mui/material"
import ShiftContext from "../contexts/ShiftContext"
import CheckIcon from "@mui/icons-material/Check"
import { UserContext } from "../contexts/UserContext"
import dayjs from "dayjs"
import duration from "dayjs/plugin/duration"
import Timer from "./Timer"
import { useAuth0 } from "@auth0/auth0-react"
import NativeLayerContext from "../contexts/NativeLayerContext"
import { sendNativeLayerEvent } from "../utils/nativeLayerCommunication/sendNativeLayerEvent"
import { EFunctionalityOverrides, ENativeLayerOutgoingEventType } from "../utils/nativeLayerCommunication/interfaces"
import { useMutation } from "@apollo/client"
import { UPDATE_SHIFT_STATUS_MUTATION } from "../entities/shifts/graphql/mutations"
import { getFormattedTime } from "../entities/tracking/helpers"
import { useTranslation } from "react-i18next"

dayjs.extend(duration)

interface ShiftButtonProps {
    text: string
    onClick?: () => void
    isConfirmationPending?: boolean
    bgColor?: string
}

const TrackingTile = () => {
    const { t } = useTranslation()
    // Contexts
    const shiftContext = useContext(ShiftContext)
    const userContext = useContext(UserContext)
    const nativeLayerContext = useContext(NativeLayerContext)

    // States
    const [currentComponent, setCurrentComponent] = useState("shift-start")
    const [timer, setTimer] = useState<NodeJS.Timeout | null>(null)
    const [isShift, setIsShift] = useState(true)
    const [elapsedTime, setElapsedTime] = useState("00:00:00")

    // Auth0 Logout
    const { logout } = useAuth0()

    // Mutations
    const [updateShiftStatus] = useMutation(UPDATE_SHIFT_STATUS_MUTATION)

    // Get the start time of the shift
    const currentShiftHistory = shiftContext?.selectedShift?.shiftHistory?.find((shift) => shift.status === "ACTIVE")
    const timestamp = currentShiftHistory?.timestamp
    const startTime = getFormattedTime(timestamp)

    //endtime should be in Date formate
    const endTime = new Date()
    const endTimeFormatted = getFormattedTime(endTime)

    // Handle Logout
    function handleLogout(): void {
        localStorage.clear()
        if (nativeLayerContext.nativeFunctionalityOverrides?.includes(EFunctionalityOverrides.AUTH)) {
            sendNativeLayerEvent({ type: ENativeLayerOutgoingEventType.LOGOUT })
        } else {
            logout({ logoutParams: { returnTo: window.location.origin } })
        }
    }

    const handleStartShift = () => {
        if (elapsedTime === "00:00:00") {
            // Start timer only if it hasn't started
            startTimer() // Start the timer
        }
    }

    const handleEndShiftClick = () => {
        setCurrentComponent("shift-confirm")
        stopTimer()
    }

    const handleConfirmEndShift = async () => {
        setIsShift(false) // Shift ended
        setCurrentComponent("shift-ended")
        try {
            await updateShiftStatus({
                variables: {
                    id: shiftContext?.selectedShift?._id,
                    record: {
                        status: "completed",
                        driverId: userContext?.user?._id,
                        vehicleId: shiftContext?.vehicleId,
                    },
                },
            })

            userContext?.setIsUserUpdate(true)
        } catch (error) {
            console.error("Error updating shift status:", error)
        }
    }

    const startTimer = () => {
        if (!timer) {
            setTimer(setInterval(() => setElapsedTime((prev) => prev + 1), 1000))
        }
    }

    const stopTimer = () => {
        if (timer) {
            clearInterval(timer)
            setTimer(null)
        }
    }

    const ShiftBox: React.FC<{ children: React.ReactNode; bgColor?: string }> = ({ children, ...rest }) => (
        <Box
            width={"100%"}
            position={"absolute"}
            top={50}
            left={0}
            display={"flex"}
            flexDirection={"column"}
            alignItems={"center"}
            justifyContent={"space-between"}
            paddingY={5}
        >
            <Box
                display={"flex"}
                alignItems={"center"}
                flexDirection={"column"}
                justifyContent={"center"}
                bgcolor={rest.bgColor || "transparent"}
                borderRadius={5}
                paddingY={7}
                textAlign={"center"}
                width={"90%"}
                maxWidth={700}
                paddingX={2}
            >
                {children}
            </Box>
        </Box>
    )

    const ShiftButton: React.FC<ShiftButtonProps> = ({ text, onClick, isConfirmationPending, bgColor }) => {
        const theme = useTheme()
        return (
            <Box
                position={"absolute"}
                width={"90vw"}
                maxWidth={700}
                display={"flex"}
                alignItems={"center"}
                bgcolor={bgColor ? bgColor : "primary.main"}
                alignSelf={"center"}
                flexDirection={"column"}
                borderRadius={6}
                padding={"2%"}
                paddingY={3}
                textAlign={"center"}
                bottom={"11vh"}
                onClick={onClick}
            >
                <Typography variant="h5">{text}</Typography>

                {isConfirmationPending && (
                    <Avatar
                        sx={{
                            boxShadow: 10,
                            backgroundColor: theme.palette.text.primary,
                            position: "absolute",
                            right: "5%",
                            bottom: "50%",
                            height: "60px",
                            width: "60px",
                        }}
                        onClick={handleConfirmEndShift}
                    >
                        <CheckIcon fontSize="large" style={{ color: theme.palette.success.main }} />
                    </Avatar>
                )}
            </Box>
        )
    }

    const renderComponent = () => {
        switch (currentComponent) {
            case "shift-start":
                return (
                    <>
                        <Box
                            width={"100%"}
                            position={"absolute"}
                            top={50}
                            left={0}
                            display={"flex"}
                            flexDirection={"column"}
                            alignItems={"center"}
                            justifyContent={"space-between"}
                            paddingY={5}
                        >
                            <Box
                                display={"flex"}
                                alignItems={"center"}
                                flexDirection={"column"}
                                justifyContent={"center"}
                                borderRadius={5}
                                textAlign={"center"}
                                width={"90%"}
                                maxWidth={700}
                                paddingX={50}
                                sx={{
                                    paddingY: {
                                        xs: 13, // Small screen size (xs)
                                        sm: 15, // Medium screen size (sm)
                                        md: 18, // Large screen size (md)
                                        lg: 15, // Extra-large screen size (lg)
                                    },
                                    "@media (max-width: 300px)": {
                                        paddingY: 10,
                                    },
                                }}
                            >
                                <Timer
                                    isShift={isShift}
                                    handleStartTimer={handleStartShift}
                                    handleStopTimer={stopTimer}
                                    timestamp={timestamp}
                                    onTimeUpdate={setElapsedTime}
                                />
                            </Box>
                        </Box>
                        <ShiftButton text={t("tracking.endShiftLabel")} onClick={handleEndShiftClick} />
                    </>
                )
            case "shift-confirm":
                return (
                    <>
                        <ShiftBox bgColor={"primary.main"}>
                            <Typography variant="h5" style={{ fontWeight: "bold" }}>
                                {t("tracking.endShiftConfirmationPrompt")}
                            </Typography>
                            <Typography variant="h6" style={{ fontWeight: "bold" }}>
                                {t("tracking.endShiftConfirmationMessage")}
                            </Typography>
                        </ShiftBox>
                        <ShiftButton text={t("tracking.confirmButton")} isConfirmationPending={true} />
                    </>
                )
            case "shift-ended":
                return (
                    <>
                        <ShiftBox bgColor={"primary.main"}>
                            <Typography variant="h5" style={{ fontWeight: "bold" }}>
                                {t("tracking.trackingEndedMessage")}
                            </Typography>
                            <Typography variant="h6">
                                {t("tracking.startTimeLabel")}: {startTime}
                            </Typography>
                            <Typography variant="h6">
                                {t("tracking.endTimeLabel")}: {endTimeFormatted}
                            </Typography>
                            <Typography variant="h6">
                                {t("tracking.durationLabel")}: {elapsedTime}
                            </Typography>
                        </ShiftBox>
                        <ShiftButton
                            text={t("tracking.logoutButton")}
                            bgColor={"#F87845"}
                            onClick={() => handleLogout()}
                        />
                    </>
                )

            default:
                return null
        }
    }

    return renderComponent()
}

export default TrackingTile
